import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import { useAuditor } from '@/hooks/useAuditor'
import { movimientosService } from '@/services/movimientosService'
import {
  categoriasGastoService,
  type CategoriaGastoInsert,
  type CategoriaGastoUpdate,
} from '@/services/categoriasGastoService'
import { gastosReporteService } from '@/services/gastosReporteService'
import type { Database } from '@/types/database'

type MovimientoInsert = Database['public']['Tables']['movimientos']['Insert']
type MovimientoUpdate = Database['public']['Tables']['movimientos']['Update']

/** Filtros propios del listado de gastos (sin exponer el tipo 'Gasto' al caller). */
export interface FiltrosGasto {
  cuentaId?: string
  categoriaId?: string
  fechaDesde?: string
  fechaHasta?: string
  mesAnio?: string
  estadoPago?: 'pagado' | 'pendiente'
}

export const gastosKeys = {
  all: ['gastos'] as const,
  lists: () => [...gastosKeys.all, 'list'] as const,
  list: (organizacionId: string | null, filtros?: FiltrosGasto) =>
    [...gastosKeys.lists(), organizacionId, filtros] as const,
}

export const categoriasGastoKeys = {
  all: ['categorias-gasto'] as const,
  list: (organizacionId: string | null, soloActivas: boolean) =>
    [...categoriasGastoKeys.all, organizacionId, soloActivas] as const,
}

export const gastosReporteKeys = {
  all: ['gastos-reporte'] as const,
  mensuales: (organizacionId: string | null, desde?: string) =>
    [...gastosReporteKeys.all, 'mensuales', organizacionId, desde] as const,
}

const invalidateGastos = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: gastosKeys.lists() })
  // El saldo de las cuentas depende de los gastos pagados (vista_saldos_cuentas)
  queryClient.invalidateQueries({ queryKey: ['cuentas'] })
  // El reporte mensual (vista_gastos_mensuales) también depende de los gastos
  queryClient.invalidateQueries({ queryKey: gastosReporteKeys.all })
}

/** Lista las categorías de gasto de la organización activa. */
export function useCategoriasGasto(soloActivas = true) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: categoriasGastoKeys.list(organizacionId, soloActivas),
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return categoriasGastoService.listar(supabase!, organizacionId, soloActivas)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/** Lista los gastos internos del estudio (movimientos tipo 'Gasto') de la org activa. */
export function useGastos(filtros?: FiltrosGasto) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: gastosKeys.list(organizacionId, filtros),
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return movimientosService.listar(supabase!, organizacionId, {
        tipo: 'Gasto',
        cuentaId: filtros?.cuentaId,
        fechaDesde: filtros?.fechaDesde,
        fechaHasta: filtros?.fechaHasta,
        mesAnio: filtros?.mesAnio,
        estadoPago: filtros?.estadoPago,
      })
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/** Reporte: gastos del estudio agregados por categoría/moneda/mes (vista_gastos_mensuales). */
export function useGastosMensuales(desde?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: gastosReporteKeys.mensuales(organizacionId, desde),
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return gastosReporteService.obtenerGastosMensuales(supabase!, organizacionId, desde)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/** Crea un gasto interno. Solo `contador_principal` (assertPermiso 'crearGasto'). */
export function useCrearGasto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const { crear } = useAuditor()

  return useMutation({
    mutationFn: (gasto: Omit<MovimientoInsert, 'organizacion_id'>) => {
      assertPermiso(puede, 'crearGasto')
      if (!organizacionId) throw new Error('Sin organización activa')
      return movimientosService.crear(supabase!, {
        ...gasto,
        organizacion_id: organizacionId,
      })
    },
    onSuccess: (data) => {
      invalidateGastos(queryClient)
      crear('movimiento', data.id, {
        tipo: data.tipo,
        concepto: data.concepto,
        debe: data.debe,
        categoria_id: data.categoria_id,
        estado_pago: data.estado_pago,
      })
    },
  })
}

/** Parámetros de una transferencia entre cuentas propias (ORR-133). */
export interface CrearTransferenciaParams {
  cuentaOrigenId: string
  cuentaDestinoId: string
  monto: number
  fecha: string
  observaciones?: string | null
}

/**
 * Crea una transferencia entre cuentas propias (par atómico egreso+ingreso vía RPC).
 * Reusa el permiso 'crearGasto' (decisión ORR-133).
 */
export function useCrearTransferencia() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const { crear } = useAuditor()

  return useMutation({
    mutationFn: (params: CrearTransferenciaParams) => {
      assertPermiso(puede, 'crearGasto')
      if (!organizacionId) throw new Error('Sin organización activa')
      return movimientosService.crearTransferencia(supabase!, {
        organizacionId,
        ...params,
      })
    },
    onSuccess: (egresoId, params) => {
      invalidateGastos(queryClient)
      crear('movimiento', egresoId, {
        es_transferencia: true,
        cuenta_origen_id: params.cuentaOrigenId,
        cuenta_destino_id: params.cuentaDestinoId,
        monto: params.monto,
      })
    },
  })
}

/** Edita un gasto interno. Solo `contador_principal` (assertPermiso 'editarGasto'). */
export function useEditarGasto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const { editar } = useAuditor()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MovimientoUpdate }) => {
      assertPermiso(puede, 'editarGasto')
      if (!organizacionId) throw new Error('Sin organización activa')
      // actualizarGasto restringe el UPDATE a tipo='Gasto' + org (no toca Deuda/Pago de cliente).
      return movimientosService.actualizarGasto(supabase!, organizacionId, id, data)
    },
    onSuccess: (data) => {
      invalidateGastos(queryClient)
      editar('movimiento', data.id, {
        concepto: data.concepto,
        debe: data.debe,
        estado_pago: data.estado_pago,
      })
    },
  })
}

/** Elimina un gasto interno. Solo `contador_principal` (assertPermiso 'eliminarGasto'). */
export function useEliminarGasto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const { eliminar: auditarEliminar } = useAuditor()

  return useMutation({
    mutationFn: async (id: string) => {
      assertPermiso(puede, 'eliminarGasto')
      const gasto = await movimientosService.obtenerPorId(supabase!, id)
      // ORR-218: la RPC limpia server-side el comprobante adjunto; el service borra su archivo de Storage.
      await movimientosService.eliminar(supabase!, id)
      return gasto
    },
    onSuccess: (data) => {
      invalidateGastos(queryClient)
      auditarEliminar('movimiento', data.id, {
        concepto: data.concepto,
        tipo: data.tipo,
      })
    },
  })
}

/** Crea una categoría de gasto. Solo `contador_principal` (assertPermiso 'gestionarCategoriasGasto'). */
export function useCrearCategoriaGasto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (categoria: Omit<CategoriaGastoInsert, 'organizacion_id'>) => {
      assertPermiso(puede, 'gestionarCategoriasGasto')
      if (!organizacionId) throw new Error('Sin organización activa')
      return categoriasGastoService.crear(supabase!, {
        ...categoria,
        organizacion_id: organizacionId,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoriasGastoKeys.all })
    },
  })
}

/** Actualiza una categoría de gasto. Solo `contador_principal` (assertPermiso 'gestionarCategoriasGasto'). */
export function useActualizarCategoriaGasto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategoriaGastoUpdate }) => {
      assertPermiso(puede, 'gestionarCategoriasGasto')
      if (!organizacionId) throw new Error('Sin organización activa')
      return categoriasGastoService.actualizar(supabase!, organizacionId, id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoriasGastoKeys.all })
      // nombre/color/tipo de categoría se reflejan en el reporte (vista_gastos_mensuales)
      queryClient.invalidateQueries({ queryKey: gastosReporteKeys.all })
    },
  })
}

/** Soft-delete (activa=false) de una categoría. No borra físicamente (preserva gastos viejos). */
export function useDesactivarCategoriaGasto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'gestionarCategoriasGasto')
      if (!organizacionId) throw new Error('Sin organización activa')
      return categoriasGastoService.desactivar(supabase!, organizacionId, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoriasGastoKeys.all })
    },
  })
}
